import moment from 'moment-timezone'

const WEEK = 7 * 24 * 60 * 60 * 1000
export const addWeeks = (date, weeks) => {
  const newDate = new Date(date)
  newDate.setTime(newDate.getTime() + weeks * WEEK)
  return newDate
}

export const range = (start, end) => {
  function* rangeHelper(start, end, step = 1) {
    let current = start
    while (current < end) {
      yield current
      current += step
    }
  }
  return Array.from(rangeHelper(start, end))
}

const DEFAULT_HOURS = range(8, 21).map(h => h * 100)
const MIN_LENGTH = 2

const intersect = date => ({ start_dt: startTime }) =>
  new Date(date).toDateString() === startTime.toDateString()

export const scheduleToModifier = schedule => date =>
  schedule.filter(intersect(date)).length > 0

export const scheduleToModifierInverted = schedule => date =>
  schedule.filter(intersect(date)).length === 0

export const toLocal = dt => moment.tz(dt, 'Europe/Stockholm')

export const stripTimezones = schedule =>
  schedule.map(({ start_dt: startDt, end_dt: endDt }) => ({
    start_dt: toLocal(startDt).toDate(),
    end_dt: toLocal(endDt).toDate(),
  }))

export const availableEndTimes = (schedule, startDate, startTime) => {
  const hasSchedule = typeof schedule !== 'undefined'
  if (!hasSchedule) {
    return DEFAULT_HOURS
  }
  if (!startTime) {
    return []
  }
  const startHour = parseInt(startTime.replace(':', '').slice(0, 4), 10)
  const daySchedule = schedule.filter(intersect(startDate))
  const hourRanges = daySchedule.map(({ start_dt: startDt, end_dt: endDt }) =>
    range(startDt.getHours(), endDt.getHours() + 1)
      // Extend array to include half hours
      .reduce((r, a) => r.concat(a * 100, a * 100 + 30), [])
      // Remove last half hour
      .filter((item, i, arr) => arr.length - 1 !== i),
  )

  const [selectedRange] = hourRanges.filter(r => r.indexOf(startHour) !== -1)
  if (selectedRange) {
    const startIndex = selectedRange.indexOf(startHour)
    return selectedRange.slice(startIndex + MIN_LENGTH * 2)
  }
  return []
}

export const availableTimes = (
  schedule,
  startDate,
  count,
  shortCircuit = false,
) => {
  // console.log(startDate, count)
  const hasSchedule = typeof schedule !== 'undefined'
  if (!hasSchedule) {
    return DEFAULT_HOURS
  }
  if (!startDate) {
    return []
  }
  const scheduleHours = range(0, count).map(index => {
    const currentDate = addWeeks(startDate, index)
    const daySchedule = schedule.filter(intersect(currentDate))
    // Build range of hours
    const hourRanges = daySchedule.map(({ start_dt: startDt, end_dt: endDt }) =>
      range(startDt.getHours(), endDt.getHours() + 1)
        // Extend array to include half hours
        .reduce((r, a) => r.concat(a * 100, a * 100 + 30), []),
    )
    return Array.from(new Set(Array.prototype.concat(...hourRanges)))
  })
  const hourMap = scheduleHours
    // Filter available hours
    .reduce(
      (a, b) => Array.from(new Set([...a])).filter(x => b.includes(x)),
      range(0, 24).reduce((r, a) => r.concat(a * 100, a * 100 + 30), []),
    )
    // Sort hours
    .sort((a, b) => a - b)
    // Build map of whole hour as key and list of half hours as value
    .reduce((r, a) => {
      const hour = parseInt(a / 100, 10)
      if (r.has(hour)) {
        r.get(hour).push(a)
      } else {
        r.set(hour, [a])
      }
      return r
    }, new Map())

  const hours = [].concat(...hourMap.values())
  // We have now a sorted list of available hours, but need to take the
  // minimum activity length requirement into account. We therefore remove
  // all times that cannot be used to form an activity that fulfill this
  // requirement.
  const validHours = []
  for (let i = 0; i < hours.length; i += 1) {
    const minLength = MIN_LENGTH * 2 + 1
    const hour = hours[i]
    const seq = hours.slice(i, i + minLength)
    if (seq.length === minLength) {
      const paddedHour = `0000${hour}`.slice(-4)
      const formattedHour = `${paddedHour.slice(0, 2)}:${paddedHour.slice(
        2,
      )}:00`
      const endTimes = availableEndTimes(schedule, startDate, formattedHour)
      if (endTimes.length === 0) {
        continue
      }
      validHours.push(hour)
      if (shortCircuit) {
        break
      }
    }
  }
  return Array.prototype.concat(...validHours)
}



// WEBPACK FOOTER //
// ../source/utilities/schedules.js